<template>
  <div class="ui-content dashboard attendance-list-page">
    <div class="attendance-top-bar">
      <div class="attendance-top-bar-inner">
        <div class="date-range-filter">
          <v-menu
            v-model="dateRangeMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="290px"
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                :value="formattedDateRange"
                placeholder="Select date range"
                outlined
                dense
                hide-details
                readonly
                v-on="on"
                append-icon="mdi-calendar"
                @click:append="dateRangeMenu = true"
                clearable
                @click:clear="onClearDateRange"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="dateRange"
              range
              @input="onDateRangeInput"
            ></v-date-picker>
          </v-menu>
        </div>
        <v-btn class="cust-btn" color="primary" @click="goToMarkAttendance">
          Mark Attendance
        </v-btn>
      </div>
    </div>
    <v-card class="mx-auto normal-card autohg">
      <div class="data-table-section">
        <v-data-table
          :headers="headers"
          :items="attendanceList"
          fixed-header
          hide-default-header
          hide-default-footer
          class="elevation-2 cust-table"
          :items-per-page="-1"
        >
          <template v-slot:header="{ props: { headers } }">
            <TableHeader
              :headers="headers"
              :onHeaderAction="onHeaderAction"
              :orderBy="orderBy"
              :onSortChange="onSortChange"
            />
          </template>

          <template v-slot:body="{ items }">
            <tbody>
              <tr v-for="item in items" :key="item.id">
                <td>{{ formatDate(item.date) }}</td>
                <td class="text-center">{{ item.center_name }}</td>
                <td class="text-center">{{ item.batch_name }}</td>
                <td>{{ item.subject_name }}</td>
                <td class="text-center">{{ item.present_count }}/{{ item.total_count }}</td>
                <td>{{ item.marked_by_name }}</td>
                <td class="blank-80 cp3-edit-delete-icon">
                  <v-tooltip v-if="canEdit" left content-class="cust-tooltip">
                    <template v-slot:activator="{ on }">
                      <v-icon
                        class="pencil-icon"
                        v-on="on"
                        @click="onEditClick(item)"
                      >mdi-pencil</v-icon>
                    </template>
                    <span>Edit Attendance</span>
                  </v-tooltip>
                  <v-tooltip left content-class="cust-tooltip">
                    <template v-slot:activator="{ on }">
                      <v-icon
                        class="view-icon"
                        v-on="on"
                        @click="onViewClick(item)"
                      >mdi-eye</v-icon>
                    </template>
                    <span>View Attendance</span>
                  </v-tooltip>
                </td>
              </tr>
              <tr v-if="!items.length && hasLoaded">
                <td :colspan="headers.length" class="text-center pa-6 grey--text">
                  No attendance records found.
                </td>
              </tr>
            </tbody>
          </template>
        </v-data-table>
      </div>
      <div class="pagination-section" v-if="totalPages > 1">
        <Pagination
          :totalPage="totalPages"
          :currentPage="currentPage"
          :onPageChange="onPageChange"
        />
      </div>
    </v-card>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import constants from "@/utils/constants.js";

const user = JSON.parse(sessionStorage.getItem("user"));

export default {
  name: "AttendanceList",
  data() {
    return {
      attendanceList: [],
      centers: [],
      batches: [],
      subjects: [],
      selectedCenterIds: [],
      selectedBatchIds: [],
      selectedSubjectIds: [],
      hasLoaded: false,
      currentPage: 1,
      pageSize: 10,
      totalRecords: 0,

      dateRange: [],
      dateRangeMenu: false,

      orderBy: {
        field: "date",
        order: "DESC",
      },
    };
  },
  computed: {
    canEdit() {
      return user && (Number(user.role_id) === constants.ROLES_ID.CO || Number(user.role_id) === constants.ROLES_ID.ID);
    },
    totalPages() {
      return Math.ceil(this.totalRecords / this.pageSize);
    },
    sortedDateRange() {
      if (!this.dateRange || !this.dateRange.length) return [];
      return [...this.dateRange].sort();
    },
    formattedDateRange() {
      if (!this.sortedDateRange.length) return "";
      const fmt = (d) => { const p = d.split("-"); return p[2] + "-" + p[1] + "-" + p[0]; };
      if (this.sortedDateRange.length === 1) return fmt(this.sortedDateRange[0]);
      return fmt(this.sortedDateRange[0]) + " ~ " + fmt(this.sortedDateRange[1]);
    },
    batchHeader() {
      if (this.selectedCenterIds.length) {
        return {
          text: "Batch",
          value: "batch_name",
          type: "multidropdown",
          options: this.batches,
          class: "text-center",
          allText: "All Batches",
        };
      }
      return { text: "Batch", value: "batch_name", class: "text-center" };
    },
    subjectHeader() {
      if (this.selectedBatchIds.length) {
        return {
          text: "Subject",
          value: "subject_name",
          type: "multidropdown",
          options: this.subjects,
          allText: "All Subjects",
        };
      }
      return { text: "Subject", value: "subject_name" };
    },
    headers() {
      return [
        { text: "Date", value: "date" },
        {
          text: "Center",
          value: "center_name",
          type: "multidropdown",
          options: this.centers,
          class: "text-center",
          allText: "All Centers",
        },
        this.batchHeader,
        this.subjectHeader,
        { text: "Present/Total", value: "present_count", hideSort: true, class: "text-center" },
        { text: "Marked By", value: "marked_by_name", hideSort: true },
        { text: "", value: "", type: "blank", class: "" },
      ];
    },
  },
  mounted() {
    this.requestGetCenters();
    this.loadAttendanceList();
  },
  methods: {
    ...mapActions({
      getCenters: "centerAndBatchStore/getCenters",
      getCentersByUser: "centerAndBatchStore/getCentersByUser",
      getBatches: "centerAndBatchStore/getBatches",
      getAttendanceList: "attendanceStore/getAttendanceList",
    }),

    formatDate(dateStr) {
      if (!dateStr) return "";
      const parts = dateStr.split("-");
      return parts[2] + "-" + parts[1] + "-" + parts[0];
    },

    requestGetCenters() {
      const action =
        user && (Number(user.role_id) === constants.ROLES_ID.CO || Number(user.role_id) === constants.ROLES_ID.TR)
          ? "getCentersByUser"
          : "getCenters";

      this[action]()
        .then((response) => {
          let list = [];
          if (response && response.length) {
            list = response;
          } else if (response && response.list && response.list.length) {
            list = response.list;
          }
          this.centers = list.map((x) => ({
            text: x.name,
            value: x.id,
            checked: false,
          }));
        })
        .catch(() => {});
    },

    loadBatches(centerIds) {
      this.getBatches({ center_id: centerIds })
        .then((response) => {
          if (response && response.list && response.list.length) {
            this.batches = response.list.map((x) => ({
              text: x.name,
              value: x.id,
              checked: false,
              subject_ids: x.subject_ids ? String(x.subject_ids) : "",
              subjects: x.subjects || "",
            }));
          }
        })
        .catch(() => {});
    },

    loadSubjects(batchIds) {
      var subjectMap = {};
      this.batches.forEach(function(batch) {
        if (batchIds.indexOf(batch.value) > -1 && batch.subject_ids && batch.subjects) {
          var ids = batch.subject_ids.split(",");
          var names = batch.subjects.split(", ");
          ids.forEach(function(id, i) {
            if (id && !subjectMap[id]) {
              subjectMap[id] = names[i] || "";
            }
          });
        }
      });
      this.subjects = Object.keys(subjectMap).map(function(id) {
        return { text: subjectMap[id], value: Number(id), checked: false };
      });
    },

    onClearDateRange() {
      this.dateRange = [];
      this.currentPage = 1;
      this.loadAttendanceList();
    },

    onDateRangeInput(val) {
      if (val && val.length === 2) {
        this.dateRangeMenu = false;
        this.currentPage = 1;
        this.loadAttendanceList();
      }
    },

    loadAttendanceList() {
      this.$root.showLoader = true;
      this.hasLoaded = false;

      const params = {
        pagination: {
          pageSize: this.pageSize,
          page: this.currentPage,
        },
      };

      const sorted = this.sortedDateRange;
      if (sorted.length >= 1) params.date_from = sorted[0];
      if (sorted.length >= 2) params.date_to = sorted[1];

      if (this.orderBy) {
        params.orderBy = this.orderBy;
      }

      // Send selected filter IDs to backend
      if (this.selectedCenterIds.length && this.selectedCenterIds.length !== this.centers.length) {
        params.center_ids = this.selectedCenterIds;
      }
      if (this.selectedBatchIds.length && this.selectedBatchIds.length !== this.batches.length) {
        params.batch_ids = this.selectedBatchIds;
      }
      if (this.selectedSubjectIds.length && this.selectedSubjectIds.length !== this.subjects.length) {
        params.subject_ids = this.selectedSubjectIds;
      }

      this.getAttendanceList(params)
        .then((response) => {
          this.$root.showLoader = false;
          this.hasLoaded = true;
          this.attendanceList = (response && response.list) || [];
          var pg = response && response.pagination;
          this.totalRecords = pg ? pg.rowCount : 0;
        })
        .catch(() => {
          this.$root.showLoader = false;
          this.hasLoaded = true;
          this.attendanceList = [];
        });
    },

    onPageChange(page) {
      this.currentPage = page;
      this.loadAttendanceList();
    },

    onHeaderAction(e) {
      if (
        e.header &&
        e.header.type === "multidropdown" &&
        e.header.value === "center_name"
      ) {
        this.selectedCenterIds = e.data.selectedItems;
        // Reset batch and subject filters, reload batches for selected centers
        this.selectedBatchIds = [];
        this.selectedSubjectIds = [];
        this.batches = [];
        this.subjects = [];
        if (this.selectedCenterIds.length) {
          this.loadBatches(this.selectedCenterIds);
        }
        this.currentPage = 1;
        this.loadAttendanceList();
      } else if (
        e.header &&
        e.header.type === "multidropdown" &&
        e.header.value === "batch_name"
      ) {
        this.selectedSubjectIds = [];
        this.subjects = [];
        this.selectedBatchIds = e.data.selectedItems;
        var self = this;
        this.$nextTick(function() {
          if (self.selectedBatchIds.length) {
            self.loadSubjects(self.selectedBatchIds);
          }
        });
        this.currentPage = 1;
        this.loadAttendanceList();
      } else if (
        e.header &&
        e.header.type === "multidropdown" &&
        e.header.value === "subject_name"
      ) {
        this.selectedSubjectIds = e.data.selectedItems;
        this.currentPage = 1;
        this.loadAttendanceList();
      }
    },

    onSortChange() {
      this.currentPage = 1;
      this.loadAttendanceList();
    },

    goToMarkAttendance() {
      this.$router.push({ name: "mark-attendance" });
    },

    onEditClick(item) {
      this.$router.push({
        name: "mark-attendance",
        query: { attendance_id: item.id },
      });
    },

    onViewClick(item) {
      this.$router.push({
        name: "mark-attendance",
        query: { attendance_id: item.id, mode: "view" },
      });
    },
  },
};
</script>
<style>
.attendance-list-page {
  display: flex;
  flex-direction: column;
  height: 100%;
}
.attendance-list-page .attendance-top-bar {
  flex-shrink: 0;
  margin-bottom: 12px;
}
.attendance-list-page .attendance-top-bar-inner {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
}
.attendance-list-page .normal-card.autohg {
  flex: 1;
  width: 100%;
  height: auto !important;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.attendance-list-page .data-table-section {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.attendance-list-page .cust-table {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.attendance-list-page .v-data-table__wrapper {
  flex: 1;
  overflow-y: auto !important;
}
.attendance-list-page .date-range-filter {
  width: 270px;
}
.attendance-list-page .date-range-filter .v-input {
  font-size: 13px;
}
.attendance-list-page .cust-table table {
  min-width: 700px;
}
.attendance-list-page .cust-table thead.v-data-table-header th {
  background: #FAFAFA !important;
}
.attendance-list-page .pagination-section {
  padding: 10px 0;
  display: flex;
  justify-content: center;
}
</style>
